if (!customElements.get('variant-table')) {
  customElements.define(
    'variant-table',
    class VariantTable extends HTMLElement {
      constructor() {
        super();
        this.handleInput = this.handleInput.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleSpinnerClick = this.handleSpinnerClick.bind(this);
        this.handleWindowLoad = this.handleWindowLoad.bind(this);
        this.handleDiscountTableMutations = this.handleDiscountTableMutations.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id');
        this.formId = this.getAttribute('data-form-id');
        this.totalLabel = this.getAttribute('data-total-label') || 'Total';
        this.quantityEl = this.querySelector('[data-variant-table-quantity]');
        this.totalEl = this.querySelector('[data-variant-table-total]');
        this.originalTotalEl = this.querySelector('[data-variant-table-original-total]');
        this.totalWrapEl = this.querySelector('[data-variant-table-total-wrap]');
        this.offerStatusEl = this.querySelector('[data-variant-table-offer-status]');
        this.offerRemainingEl = this.querySelector('[data-variant-table-offer-remaining]');
        this.offerCompleteEl = this.querySelector('[data-variant-table-offer-complete]');
        this.inputs = this.querySelectorAll('[data-variant-table-input]');
        this.isBuyXOffer = this.dataset.buyXOffer === 'true';
        this.requiredQuantity = parseInt(this.dataset.requiredQuantity, 10) || 0;
        this.remainingMessage = this.dataset.remainingMessage || '__QUANTITY__ remaining.';
        this.incompleteError = this.dataset.incompleteError || 'Please complete the required quantity.';
        this.addError = this.dataset.addError || 'The complete selection could not be added. Please try again.';
        this.rollbackError = this.dataset.rollbackError || 'Please check your cart before trying again.';
        this.emptyError = this.dataset.emptyError || 'Please enter a quantity for at least one variant.';
        this.productInfo = this.closest('product-info') || this.closest('[data-section]') || document;
        this.variants = this.parseVariants();
        this.skuRecency = [];
        this.skuWrapper = this.productInfo.querySelector('[data-gdt-sku-wrapper]');
        this.skuValue = this.productInfo.querySelector('[data-gdt-sku-value]');
        this.originalSku = this.skuValue?.textContent || '';
        this.originalSkuDisplay = this.skuWrapper?.style.display || '';
        this.mainPriceElement = this.productInfo.querySelector(`[id="price-${this.sectionId}"]`);
        this.originalMainPriceMarkup = this.mainPriceElement?.innerHTML || '';
        this.isMainPriceSynced = false;
        this.discountTableSelectors = '.shopacado-discount-table';
        this.discountTableWrapperSelectors = '.shopacado-discount-table-block';
        this.discountTiers = [];
        this.discountRefreshTimeout = null;
        this.discountRetryTimeouts = [];
        this.discountTableObserver = null;

        this.inputs.forEach((input) => {
          input.addEventListener('input', this.handleInput);
          input.addEventListener('change', this.handleInput);
        });

        this.querySelectorAll('[data-variant-table-step]').forEach((button) => {
          button.addEventListener('click', this.handleSpinnerClick);
        });

        this.productForm = document.getElementById(this.formId);
        this.variantIdInput = this.productForm?.querySelector('[name="id"]') || null;
        this.originalVariantId = this.variantIdInput?.value || '';
        if (this.productForm) {
          this.productForm.addEventListener('submit', this.handleFormSubmit, true);
        }

        if (this.isBuyXOffer) {
          this.updateTotals();
        } else {
          this.setupDiscountTableSync();
        }
      }

      disconnectedCallback() {
        this.inputs.forEach((input) => {
          input.removeEventListener('input', this.handleInput);
          input.removeEventListener('change', this.handleInput);
        });

        this.querySelectorAll('[data-variant-table-step]').forEach((button) => {
          button.removeEventListener('click', this.handleSpinnerClick);
        });

        if (this.productForm) {
          this.productForm.removeEventListener('submit', this.handleFormSubmit, true);
        }

        if (this.discountRefreshTimeout) {
          window.clearTimeout(this.discountRefreshTimeout);
        }

        if (this.discountRetryTimeouts.length) {
          this.discountRetryTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
          this.discountRetryTimeouts = [];
        }

        if (this.discountTableObserver) {
          this.discountTableObserver.disconnect();
        }

        window.removeEventListener('load', this.handleWindowLoad);
      }

      get cartDrawerElement() {
        return document.querySelector('cart-drawer');
      }

      get submitButtonElement() {
        if (!this.productForm) return null;
        return this.productForm.querySelector('[type="submit"]');
      }

      handleInput(event) {
        const input = event.target;
        let value = parseInt(input.value, 10);

        if (isNaN(value) || value < 0) {
          value = 0;
        }

        const max = input.max ? parseInt(input.max, 10) : null;
        if (max !== null && value > max) {
          value = max;
        }

        if (this.isBuyXOffer) {
          const otherQuantity = Array.from(this.inputs).reduce((total, currentInput) => {
            if (currentInput === input) return total;
            return total + (parseInt(currentInput.value, 10) || 0);
          }, 0);
          value = Math.min(value, Math.max(0, this.requiredQuantity - otherQuantity));
        }

        this.setInputValue(input, value);
        this.updateSkuRecency(input, value);
        this.updateTotals();
      }

      handleSpinnerClick(event) {
        const direction = event.currentTarget.dataset.variantTableStep;
        const input = event.currentTarget.closest('.variant-table__input-wrap')?.querySelector('[data-variant-table-input]');

        if (!input) return;

        const currentValue = parseInt(input.value, 10) || 0;
        const nextValue = direction === 'up' ? currentValue + 1 : currentValue - 1;
        const normalizedValue = Math.max(0, nextValue);
        const max = input.max ? parseInt(input.max, 10) : null;
        let finalValue = max !== null && normalizedValue > max ? max : normalizedValue;

        if (this.isBuyXOffer) {
          const otherQuantity = Array.from(this.inputs).reduce((total, currentInput) => {
            if (currentInput === input) return total;
            return total + (parseInt(currentInput.value, 10) || 0);
          }, 0);
          finalValue = Math.min(finalValue, Math.max(0, this.requiredQuantity - otherQuantity));
        }

        this.setInputValue(input, finalValue);
        this.updateSkuRecency(input, finalValue);
        this.updateTotals();
      }

      setInputValue(input, value) {
        input.value = value > 0 ? value : '';
      }

      parseVariants() {
        const variantsJson = this.querySelector('[data-variant-table-json]');
        if (!variantsJson) return new Map();

        try {
          const variants = JSON.parse(variantsJson.textContent);
          return new Map(variants.map((variant) => [String(variant.id), variant]));
        } catch (error) {
          console.error('Unable to parse variant table data.', error);
          return new Map();
        }
      }

      updateSkuRecency(input, quantity) {
        const variantId = String(input.dataset.variantId);
        this.skuRecency = this.skuRecency.filter((id) => id !== variantId);

        if (quantity > 0) {
          this.skuRecency.push(variantId);
        }
      }

      getActiveSkuVariantId() {
        const activeVariantIds = new Set(
          Array.from(this.inputs)
            .filter((input) => (parseInt(input.value, 10) || 0) > 0)
            .map((input) => String(input.dataset.variantId))
        );

        this.skuRecency = this.skuRecency.filter((variantId) => activeVariantIds.has(variantId));

        if (this.skuRecency.length) {
          return this.skuRecency[this.skuRecency.length - 1];
        }

        const fallbackVariantId = activeVariantIds.values().next().value;
        if (fallbackVariantId) {
          this.skuRecency.push(fallbackVariantId);
        }

        return fallbackVariantId || null;
      }

      restoreSku() {
        if (!this.skuWrapper || !this.skuValue) return;

        this.skuValue.textContent = this.originalSku;
        this.skuWrapper.style.display = this.originalSkuDisplay;
      }

      setMainVariantId(variantId) {
        if (!this.variantIdInput) return;

        const nextVariantId = variantId ? String(variantId) : this.originalVariantId;
        if (!nextVariantId || this.variantIdInput.value === nextVariantId) return;

        this.variantIdInput.value = nextVariantId;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncSku() {
        if (!this.skuWrapper || !this.skuValue) return;

        const variantId = this.getActiveSkuVariantId();
        if (!variantId) {
          this.restoreSku();
          return;
        }

        const variant = this.variants.get(variantId);
        const sku = variant?.sku?.trim();

        if (!sku) {
          this.skuValue.textContent = '';
          this.skuWrapper.style.display = 'none';
          return;
        }

        this.skuValue.textContent = sku;
        this.skuWrapper.style.display = 'inline-flex';
      }

      syncMainVariantInput() {
        const variantId = this.getActiveSkuVariantId();
        this.setMainVariantId(variantId);
      }

      setupDiscountTableSync() {
        this.refreshDiscountTiers();

        [250, 1000].forEach((delay) => {
          this.discountRetryTimeouts.push(
            window.setTimeout(() => {
              this.refreshDiscountTiers();
            }, delay)
          );
        });

        if (document.readyState === 'complete') {
          this.scheduleDiscountTierRefresh(0);
        } else {
          window.addEventListener('load', this.handleWindowLoad, { once: true });
        }

        const observerTarget = this.productInfo === document ? document.body : this.productInfo;
        if (observerTarget && 'MutationObserver' in window) {
          this.discountTableObserver = new MutationObserver(this.handleDiscountTableMutations);
          this.discountTableObserver.observe(observerTarget, {
            attributes: true,
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      handleWindowLoad() {
        this.scheduleDiscountTierRefresh(0);
      }

      handleDiscountTableMutations(mutations) {
        if (!this.shouldRefreshDiscountTiers(mutations)) return;
        this.scheduleDiscountTierRefresh();
      }

      shouldRefreshDiscountTiers(mutations) {
        return mutations.some((mutation) => {
          if (this.nodeMatchesDiscountTable(mutation.target)) {
            return true;
          }

          return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => this.nodeMatchesDiscountTable(node));
        });
      }

      nodeMatchesDiscountTable(node) {
        const element = this.elementFromNode(node);
        if (!element || this.contains(element)) return false;

        const selectors = `${this.discountTableSelectors}, ${this.discountTableWrapperSelectors}`;
        if (element.matches?.(selectors)) return true;
        if (element.closest?.(selectors)) return true;

        return Boolean(element.querySelector?.(selectors));
      }

      elementFromNode(node) {
        if (!node) return null;
        if (node.nodeType === Node.ELEMENT_NODE) return node;
        return node.parentElement || null;
      }

      scheduleDiscountTierRefresh(delay = 120) {
        if (this.discountRefreshTimeout) {
          window.clearTimeout(this.discountRefreshTimeout);
        }

        this.discountRefreshTimeout = window.setTimeout(() => {
          this.refreshDiscountTiers();
          this.discountRefreshTimeout = null;
        }, delay);
      }

      refreshDiscountTiers() {
        this.discountTiers = this.parseDiscountTiers();
        this.updateTotals();
      }

      findDiscountTable() {
        return this.productInfo?.querySelector(this.discountTableSelectors) || null;
      }

      parseDiscountTiers() {
        const table = this.findDiscountTable();
        if (!table) return [];

        return window.FoxTheme?.ShopacadoPricing?.parseDiscountTable?.(table) || [];
      }

      getActiveDiscountTier(totalQty) {
        if (!this.discountTiers.length || totalQty <= 0) return null;

        return this.discountTiers.reduce((activeTier, tier) => {
          if (totalQty >= tier.minimumQuantity) {
            return tier;
          }

          return activeTier;
        }, null);
      }

      applyDiscountTier(tier, sellingTotalCents, totalQty) {
        return (
          window.FoxTheme?.ShopacadoPricing?.applyTier?.(tier, sellingTotalCents, totalQty) ?? sellingTotalCents
        );
      }

      calculateTotals() {
        let totalQty = 0;
        let sellingTotalCents = 0;
        let referenceTotalCents = 0;

        this.inputs.forEach((input) => {
          const qty = parseInt(input.value, 10) || 0;
          const price = parseInt(input.dataset.price, 10) || 0;
          const compareAtPrice = parseInt(input.dataset.compareAtPrice, 10) || 0;
          const referencePrice = compareAtPrice > price ? compareAtPrice : price;

          totalQty += qty;
          sellingTotalCents += qty * price;
          referenceTotalCents += qty * referencePrice;
        });

        const activeTier = this.getActiveDiscountTier(totalQty);
        const payableTotalCents = this.applyDiscountTier(activeTier, sellingTotalCents, totalQty);
        const hasSavings = payableTotalCents < referenceTotalCents;

        return {
          totalQty,
          payableTotalCents,
          referenceTotalCents,
          hasSavings,
        };
      }

      formatMoney(cents) {
        if (window.FoxTheme?.Currency?.formatMoney) {
          return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
        }

        return `$${(cents / 100).toFixed(2)}`;
      }

      updateTotals() {
        const totals = this.calculateTotals();
        const { totalQty, payableTotalCents, referenceTotalCents, hasSavings } = totals;

        if (this.quantityEl) {
          this.quantityEl.textContent = totalQty;
        }

        if (this.isBuyXOffer) {
          this.updateBuyXState(totalQty);
          this.syncSku();
          this.syncMainVariantInput();
          return;
        }

        if (this.totalEl) {
          this.totalEl.innerHTML = this.formatMoney(payableTotalCents);
        }

        if (this.totalWrapEl) {
          this.totalWrapEl.classList.toggle('variant-table__total-price--discounted', hasSavings);
        }

        if (this.originalTotalEl) {
          if (hasSavings) {
            this.originalTotalEl.hidden = false;
            this.originalTotalEl.innerHTML = this.formatMoney(referenceTotalCents);
          } else {
            this.originalTotalEl.hidden = true;
            this.originalTotalEl.innerHTML = '';
          }
        }

        this.syncSku();
        this.syncMainVariantInput();
        this.syncMainPrice(totals);
      }

      updateBuyXState(totalQty) {
        const isComplete = totalQty === this.requiredQuantity;
        const remaining = Math.max(0, this.requiredQuantity - totalQty);

        if (this.offerStatusEl) {
          if (this.offerRemainingEl) {
            this.offerRemainingEl.textContent = this.remainingMessage.replace('__QUANTITY__', String(remaining));
            this.offerRemainingEl.hidden = isComplete;
          }
          if (this.offerCompleteEl) {
            this.offerCompleteEl.hidden = !isComplete;
          }
          this.offerStatusEl.classList.toggle('variant-table__offer-status--complete', isComplete);
        }

        const submitButton = this.submitButtonElement;
        if (!submitButton) return;

        submitButton.disabled = !isComplete;
        if (isComplete) {
          submitButton.removeAttribute('aria-disabled');
        } else {
          submitButton.setAttribute('aria-disabled', 'true');
        }
      }

      restoreMainPrice() {
        if (!this.mainPriceElement || !this.isMainPriceSynced) return;

        this.mainPriceElement.innerHTML = this.originalMainPriceMarkup;
        this.isMainPriceSynced = false;
      }

      syncMainPrice({ totalQty, payableTotalCents, referenceTotalCents, hasSavings }) {
        if (!this.mainPriceElement) return;

        if (totalQty <= 0) {
          this.restoreMainPrice();
          return;
        }

        const price = this.mainPriceElement.querySelector('.f-price');
        const regularPrice = price?.querySelector('.f-price__regular .f-price-item--regular');
        const salePrice = price?.querySelector('.f-price__sale .f-price-item--sale');
        const comparePrice = price?.querySelector('.f-price__sale .f-price-item--regular');
        const unitPrice = price?.querySelector('.f-price__unit-wrapper');

        if (!price || !regularPrice || !salePrice || !comparePrice) return;

        price.classList.remove('f-price--sold-out');
        unitPrice?.classList.add('hidden');

        if (hasSavings) {
          price.classList.add('f-price--on-sale');
          salePrice.innerHTML = this.formatMoney(payableTotalCents);
          salePrice.insertAdjacentText('afterbegin', `${this.totalLabel}: `);
          comparePrice.innerHTML = `<s>${this.formatMoney(referenceTotalCents)}</s>`;
        } else {
          price.classList.remove('f-price--on-sale');
          regularPrice.innerHTML = this.formatMoney(payableTotalCents);
          regularPrice.insertAdjacentText('afterbegin', `${this.totalLabel}: `);
          salePrice.innerHTML = '';
          comparePrice.innerHTML = '';
        }

        this.isMainPriceSynced = true;
      }

      getItemsToAdd() {
        const items = [];

        this.inputs.forEach((input) => {
          const qty = parseInt(input.value, 10) || 0;
          if (qty > 0) {
            items.push({
              id: parseInt(input.dataset.variantId, 10),
              quantity: qty,
            });
          }
        });

        return items;
      }

      displayFormError(message) {
        if (!this.productForm) return;

        const errorWrapper = this.productForm
          .closest('.product-form')
          ?.querySelector('.product-form__error-message-wrapper');
        const errorMessage = errorWrapper?.querySelector('.product-form__error-message');

        if (errorMessage) {
          errorWrapper.hidden = !message;
          errorMessage.hidden = !message;
          if (message) {
            errorMessage.innerText = message;
          }
        } else if (message) {
          alert(message);
        }
      }

      clearFormError() {
        this.displayFormError(false);
      }

      resetInputs() {
        this.inputs.forEach((input) => {
          input.value = '';
        });
        this.skuRecency = [];
        this.updateTotals();
      }

      async handleFormSubmit(event) {
        const items = this.getItemsToAdd();
        const isPageCart = FoxTheme.settings.cartType === 'page';
        const selectedQuantity = items.reduce((total, item) => total + item.quantity, 0);

        if (this.isBuyXOffer && selectedQuantity !== this.requiredQuantity) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.displayFormError(this.incompleteError);
          return;
        }

        if (items.length === 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.displayFormError(this.emptyError);
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const submitButton = event.submitter || this.submitButtonElement;
        if (submitButton?.getAttribute('aria-disabled') === 'true' || this.isSubmitting) return;

        this.clearFormError();
        this.lastSubmittedElement = submitButton;
        this.isSubmitting = true;

        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
          submitButton.classList.add('btn--loading');
        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
          items,
        });

        let cartBefore = null;

        try {
          if (this.isBuyXOffer) {
            cartBefore = await this.fetchCart();
          }

          const response = await fetch(FoxTheme.routes.cart_add_url, config);
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            let rollbackSucceeded = true;
            if (this.isBuyXOffer && cartBefore) {
              rollbackSucceeded = await this.rollbackOfferItems(cartBefore, items);
            }
            if (this.isBuyXOffer) {
              this.displayFormError(rollbackSucceeded ? this.addError : this.rollbackError);
              document.dispatchEvent(
                new CustomEvent('product-ajax:error', {
                  detail: { errorMessage: rollbackSucceeded ? this.addError : this.rollbackError },
                })
              );
            } else {
              this.handleCartError(parsedState);
            }
            try {
              await this.refreshCart(false);
            } catch (refreshError) {
              console.error(refreshError);
            }
            return;
          }

          const cartJson = await this.fetchCart();
          if (this.isBuyXOffer && !this.offerWasAddedCompletely(cartBefore, cartJson, items)) {
            const rollbackSucceeded = await this.rollbackOfferItems(cartBefore, items);
            try {
              await this.refreshCart(false);
            } catch (refreshError) {
              console.error(refreshError);
            }
            this.displayFormError(rollbackSucceeded ? this.addError : this.rollbackError);
            return;
          }

          if (isPageCart) {
            window.location.href = FoxTheme.routes.cart_url;
            return;
          }

          cartJson.sections = parsedState.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: parsedState },
            })
          );

          this.resetInputs();
          this.showCartDrawer();
        } catch (error) {
          console.error(error);
          let rollbackSucceeded = true;
          if (this.isBuyXOffer && cartBefore) {
            rollbackSucceeded = await this.rollbackOfferItems(cartBefore, items);
            try {
              await this.refreshCart(false);
            } catch (refreshError) {
              console.error(refreshError);
            }
          }
          this.displayFormError(rollbackSucceeded ? this.addError : this.rollbackError);
        } finally {
          this.isSubmitting = false;
          if (submitButton) {
            submitButton.classList.remove('btn--loading');
            submitButton.removeAttribute('aria-disabled');
          }
          this.updateTotals();
        }
      }

      async fetchCart() {
        const response = await fetch(`${FoxTheme.routes.cart_url}`, {
          ...FoxTheme.utils.fetchConfig('json', 'GET'),
        });
        if (!response.ok) throw new Error('Unable to read the cart.');
        return response.json();
      }

      quantityByVariant(cart, variantId) {
        return (cart?.items || []).reduce(
          (total, item) => total + (String(item.variant_id) === String(variantId) ? item.quantity : 0),
          0
        );
      }

      offerWasAddedCompletely(cartBefore, cartAfter, items) {
        if (!cartBefore || !cartAfter) return false;
        return items.every((item) => {
          const beforeQuantity = this.quantityByVariant(cartBefore, item.id);
          const afterQuantity = this.quantityByVariant(cartAfter, item.id);
          return afterQuantity - beforeQuantity === item.quantity;
        });
      }

      async rollbackOfferItems(cartBefore, items) {
        try {
          const updates = {};
          items.forEach((item) => {
            updates[String(item.id)] = this.quantityByVariant(cartBefore, item.id);
          });

          const config = FoxTheme.utils.fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify({ updates });
          const response = await fetch(FoxTheme.routes.cart_update_url, config);
          if (!response.ok) return false;

          const restoredCart = await this.fetchCart();
          return items.every(
            (item) => this.quantityByVariant(restoredCart, item.id) === this.quantityByVariant(cartBefore, item.id)
          );
        } catch (error) {
          console.error(error);
          return false;
        }
      }

      async refreshCart(open = false) {
        const cartJson = await this.fetchCart();
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open },
          })
        );
        return cartJson;
      }

      handleCartError(parsedState) {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
          source: 'variant-table',
          errors: parsedState.errors || parsedState.description,
          message: parsedState.message,
        });

        this.displayFormError(parsedState.description || parsedState.message);

        document.dispatchEvent(
          new CustomEvent('product-ajax:error', {
            detail: {
              errorMessage: parsedState.description || parsedState.message,
            },
          })
        );
      }

      showCartDrawer() {
        const quickViewModal = this.closest('quick-view-modal');

        if (quickViewModal) {
          if (this.cartDrawerElement && !this.cartDrawerElement.open) {
            document.body.addEventListener(
              quickViewModal.events.handleAfterHide,
              () => {
                setTimeout(() => {
                  this.cartDrawerElement.show(this.lastSubmittedElement);
                });
              },
              { once: true }
            );
          }
          quickViewModal.hide(true);
        } else if (this.cartDrawerElement) {
          this.cartDrawerElement.show(this.lastSubmittedElement);
        }
      }
    }
  );
}
